// 特殊計上（指導料・営業所コード集計）— 本城さん 6/30 確定情報に基づく自動計算。
//
// ■ 指導料（豊島様5月分PDFで検証済み）
//   - 指導者は「被指導者の月間個数 × 単価」を収入（売上側・課税10%）として受け取る
//   - 被指導者×便種ごとに1行（納品日=月末）。個数0でも行は出す（PDFと同じ）
//   - 金額は行ごとに円未満切り捨て（例 4.55×2,551=11,607.05→11,607）
//   - 単価は小数（4.55/9.09）のため、浮動小数の誤差を避けて「銭」単位の整数で計算する
// ■ 営業所コード集計（勝本様4月分PDFで検証済み）
//   - ヤマトCSVの指定営業所コードの宅急便/ネコポス個数合計 × 単価（例 73049 × 10円）

import type { ItemRow, YamatoRow, SagawaRow, SagawaBenkindRule, ShidoRule, OfficeSumRule } from "../../types";
import { normalizeName } from "../normalize";
import { classifySagawaCode } from "./sagawaRules";

// 単価×個数を円未満切り捨て（単価は小数2桁まで想定。銭単位の整数演算で誤差を避ける）
export function floorAmount(unit: number, qty: number): number {
  return Math.floor((Math.round(unit * 100) * qty) / 100);
}

// 被指導者の月間個数（宅急便/ネコポス）をヤマト・佐川CSVから集計
function targetQty(
  nameNormalized: string,
  yamatoRows: YamatoRow[],
  sagawaRows: SagawaRow[],
  benkindRules: SagawaBenkindRule[]
): { takkyu: number; nekopos: number } {
  let takkyu = 0;
  let nekopos = 0;
  for (const r of yamatoRows) {
    if (r.driverNameNormalized !== nameNormalized) continue;
    takkyu += r.takkyubinQty;
    nekopos += r.nekoposQty;
  }
  for (const r of sagawaRows) {
    if (r.driverNameNormalized !== nameNormalized) continue;
    const cat = classifySagawaCode(r.benkindCode, benkindRules);
    if (cat === "takkyu") takkyu += r.countQty;
    else if (cat === "nekopos") nekopos += r.countQty;
  }
  return { takkyu, nekopos };
}

// 被指導者名がCSV上の担当者名（driverNameNormalized）と1件も一致しないか判定する（設定画面の表記ゆれ警告用）。
// CSV未取込（ヤマト・佐川とも0行）のときは判定不能のため false（警告なし）を返す。
export function isTargetNameUnmatched(targetName: string, yamatoRows: YamatoRow[], sagawaRows: SagawaRow[]): boolean {
  if (yamatoRows.length === 0 && sagawaRows.length === 0) return false;
  const n = normalizeName(targetName);
  if (!n) return false;
  const matched = yamatoRows.some((r) => r.driverNameNormalized === n) || sagawaRows.some((r) => r.driverNameNormalized === n);
  return !matched;
}

// 指導料の売上itemを生成（指導者1名分）。CSV未取込（両方空）のときは呼び出し側でスキップする
export function buildShidoItems(
  rule: ShidoRule,
  yamatoRows: YamatoRow[],
  sagawaRows: SagawaRow[],
  benkindRules: SagawaBenkindRule[],
  salesDate: string
): ItemRow[] {
  const items: ItemRow[] = [];
  if (rule.fixedAmount != null && rule.fixedAmount > 0) {
    items.push({
      deliveryDate: salesDate,
      name: "指導料（毎月固定）",
      unitPrice: rule.fixedAmount,
      quantity: 1,
      amount: rule.fixedAmount,
      taxRate: "10%",
      category: "sales",
    });
  }
  for (const t of rule.targets) {
    const name = t.targetName.trim();
    if (!name) continue;
    const q = targetQty(normalizeName(name), yamatoRows, sagawaRows, benkindRules);
    if (t.unitTakkyu != null) {
      items.push({
        deliveryDate: salesDate,
        name: `指導料 ${name}　宅急便`,
        unitPrice: t.unitTakkyu,
        quantity: q.takkyu,
        amount: floorAmount(t.unitTakkyu, q.takkyu),
        taxRate: "10%",
        category: "sales",
      });
    }
    if (t.unitNekopos != null) {
      items.push({
        deliveryDate: salesDate,
        name: `指導料 ${name}　ネコポス`,
        unitPrice: t.unitNekopos,
        quantity: q.nekopos,
        amount: floorAmount(t.unitNekopos, q.nekopos),
        taxRate: "10%",
        category: "sales",
      });
    }
  }
  return items;
}

// 営業所コードの比較用正規化（先頭ゼロを吸収）。
// 2026-06月のヤマトCSVから営業所コードが5桁「73049」→6桁ゼロ埋め「073049」に変わり、
// 完全一致比較が全行外れて勝本様の通知書が作られなくなった（本城さん 7/16 報告）。
// 設定値・CSV値の両側を正規化してフォーマット変化を吸収する（全ゼロは "0" に潰れないよう1文字残す）。
function normalizeOfficeCode(code: string): string {
  const t = (code || "").trim();
  return t.replace(/^0+(?=\d)/, "");
}

// 営業所コード集計の売上itemを生成（該当営業所の行が1件も無ければ空＝通知書を作らない）
export function buildOfficeSumItems(rule: OfficeSumRule, yamatoRows: YamatoRow[], salesDate: string): ItemRow[] {
  let takkyu = 0;
  let nekopos = 0;
  let matched = false;
  const ruleCode = normalizeOfficeCode(rule.officeCode);
  for (const r of yamatoRows) {
    if (normalizeOfficeCode(r.officeCode || "") !== ruleCode) continue;
    matched = true;
    takkyu += r.takkyubinQty;
    nekopos += r.nekoposQty;
  }
  if (!matched) return [];
  const items: ItemRow[] = [];
  if (rule.unitTakkyu != null) {
    items.push({
      deliveryDate: salesDate,
      name: "宅配",
      unitPrice: rule.unitTakkyu,
      quantity: takkyu,
      amount: floorAmount(rule.unitTakkyu, takkyu),
      taxRate: "10%",
      category: "sales",
      carrier: "yamato",
      note: `営業所コード ${rule.officeCode} 集計`,
    });
  }
  if (rule.unitNekopos != null) {
    items.push({
      deliveryDate: salesDate,
      name: "メール便",
      unitPrice: rule.unitNekopos,
      quantity: nekopos,
      amount: floorAmount(rule.unitNekopos, nekopos),
      taxRate: "10%",
      category: "sales",
      carrier: "yamato",
      note: `営業所コード ${rule.officeCode} 集計`,
    });
  }
  return items;
}

// ===== 初期設定（本城さん 6/30 提供の確定情報） =====

let seq = 0;
const rid = (p: string) => `${p}-${++seq}`;

export const DEFAULT_SHIDO_RULES: ShidoRule[] = [
  {
    instructorName: "中村 雅己",
    fixedAmount: 100000,
    targets: [{ id: rid("st"), targetName: "寺山和宏", unitTakkyu: 9.09, unitNekopos: 4.55 }],
  },
  {
    instructorName: "中谷 康之",
    fixedAmount: null,
    targets: [{ id: rid("st"), targetName: "川瀬知香", unitTakkyu: 9.09, unitNekopos: 4.55 }],
  },
  {
    instructorName: "豊島 竜太郎",
    fixedAmount: null,
    targets: [
      { id: rid("st"), targetName: "兼光勇次", unitTakkyu: 4.55, unitNekopos: 4.55 },
      { id: rid("st"), targetName: "兼光由美子", unitTakkyu: 4.55, unitNekopos: 4.55 },
      { id: rid("st"), targetName: "金坂商店", unitTakkyu: 9.09, unitNekopos: 4.55 },
      { id: rid("st"), targetName: "渡辺真一", unitTakkyu: 4.55, unitNekopos: 4.55 },
      { id: rid("st"), targetName: "山本光一", unitTakkyu: 4.55, unitNekopos: 4.55 },
      { id: rid("st"), targetName: "渡辺容子", unitTakkyu: 4.55, unitNekopos: 4.55 },
    ],
  },
];

export const DEFAULT_OFFICE_SUM_RULES: OfficeSumRule[] = [
  { id: rid("os"), driverName: "勝本 和恵", officeCode: "73049", unitTakkyu: 10, unitNekopos: 10 },
];
